import { useEffect, useEffectEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    ExtraCategoriesDocument,
    IBudgetRequestDetailFragment,
    IContentTypeValue,
    UserDocument,
} from 'graphql/types';
import {
    DatePickerField,
    ExtraCategoryFormFields,
    LinkField,
    NumberField,
    SelectField,
    TextField,
} from 'common/forms/Field';
import { FormFooter } from 'common/forms/components/FormFooter';
import { Divider } from 'common/components/Divider';
import { useApolloError } from 'common/hooks/useApolloError';
import { TSelectOption } from 'common/types';
import { getExtraCategoryInitialFormValues } from 'organisation/utils/extraCategory';
import { TBudgetRequestFormValues } from 'budget/types';
import { BudgetRequestFileTable } from 'budget/components/BudgetRequestFileTable';
import { getBudgetRequestFormValidator } from 'budget/forms/validators/budgetRequestForm';

interface IProps {
    budgetRequest?: IBudgetRequestDetailFragment;
    userId: string;
    onCreate: SubmitHandler<TBudgetRequestFormValues>;
    onUpdate?: SubmitHandler<TBudgetRequestFormValues>;
    onDelete?(): void | Promise<void>;
    onClose?(): void;
}

export const BudgetRequestForm = ({
    budgetRequest,
    userId,
    onCreate,
    onUpdate,
    onDelete,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();

    const isEdit = !!budgetRequest;

    const { showApolloError } = useApolloError();

    const { data: userData, error: userError } = useQuery(UserDocument, {
        variables: { id: userId },
    });

    const { data: extraCategoriesData, error: extraCategoriesError } = useQuery(
        ExtraCategoriesDocument,
        { variables: { contentType: IContentTypeValue.BudgetRequest } }
    );

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (userError) {
            onError(userError);
        }
    }, [userError]);

    useEffect(() => {
        if (extraCategoriesError) {
            onError(extraCategoriesError);
        }
    }, [extraCategoriesError]);

    // TODO: filter groups on budget groups
    const groupOptions = useMemo<TSelectOption[]>(
        () =>
            (userData?.user?.participantGroups ?? []).map((group) => ({
                value: group.id,
                label: group.name,
            })),
        [userData?.user?.participantGroups]
    );

    const defaultValues = useMemo<TBudgetRequestFormValues>(
        () => ({
            title: budgetRequest?.title || '',
            amount: budgetRequest?.amount ?? null,
            provider: budgetRequest?.provider || '',
            // Strip the protocol, the LinkField renders it as an adornment
            link: budgetRequest?.externalUrl.replace(/(^\w+:|^)\/\//, '') || '',
            startDate: budgetRequest?.startDate
                ? DateTime.fromISO(budgetRequest.startDate)
                : null,
            endDate: budgetRequest?.endDate
                ? DateTime.fromISO(budgetRequest.endDate)
                : null,
            groupId: budgetRequest?.group.id || '',
            description: budgetRequest?.description || '',
            extraCategories: getExtraCategoryInitialFormValues(
                extraCategoriesData?.extraCategories,
                budgetRequest?.extraCategoryValues
            ),
        }),
        [budgetRequest, extraCategoriesData?.extraCategories]
    );

    const formMethods = useForm<TBudgetRequestFormValues>({
        defaultValues,
        resetOptions: { keepDirtyValues: true },
        resolver: zodResolver(getBudgetRequestFormValidator()),
    });

    const { control, handleSubmit, reset } = formMethods;

    useEffect(() => {
        reset(defaultValues, { keepDirtyValues: true });
    }, [defaultValues, reset]);

    const handleFormSubmit: SubmitHandler<TBudgetRequestFormValues> =
        isEdit && onUpdate ? onUpdate : onCreate;

    return (
        <FormProvider {...formMethods}>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                {/* noValidate: validation is handled by Zod, the `required`
                    props are only there for the label asterisks. */}
                <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
                    <Typography variant="h3">
                        {translate(
                            isEdit
                                ? 'budgetRequestForm.editTitle'
                                : 'budgetRequestForm.title'
                        )}
                    </Typography>

                    {!isEdit && (
                        <Typography sx={{ mb: 2 }}>
                            {translate('budgetRequestForm.description')}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            columnGap: 2,
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr 1fr',
                            },
                        }}
                    >
                        <TextField
                            required
                            control={control}
                            label={translate('budgetRequestForm.fields.title')}
                            name="title"
                        />

                        <NumberField
                            required
                            control={control}
                            label={translate('budgetRequestForm.fields.amount')}
                            name="amount"
                        />

                        <TextField
                            control={control}
                            label={translate(
                                'budgetRequestForm.fields.provider'
                            )}
                            name="provider"
                        />

                        <LinkField
                            control={control}
                            label={translate('budgetRequestForm.fields.link')}
                            name="link"
                        />

                        <DatePickerField
                            control={control}
                            label={translate(
                                'budgetRequestForm.fields.startDate'
                            )}
                            name="startDate"
                        />

                        <DatePickerField
                            control={control}
                            label={translate(
                                'budgetRequestForm.fields.endDate'
                            )}
                            name="endDate"
                        />
                    </Box>

                    <SelectField
                        required
                        control={control}
                        label={translate('budgetRequestForm.fields.group')}
                        name="groupId"
                        options={groupOptions}
                    />

                    <TextField
                        multiline
                        control={control}
                        label={translate(
                            'budgetRequestForm.fields.description'
                        )}
                        name="description"
                    />

                    <ExtraCategoryFormFields
                        contentType={IContentTypeValue.BudgetRequest}
                        control={control}
                        emptyMessageSx={{ mt: 2 }}
                        name="extraCategories"
                    />

                    <Divider sx={{ mt: 3 }} />

                    <Box sx={{ mt: 3 }}>
                        {budgetRequest ? (
                            <BudgetRequestFileTable
                                budgetRequestId={budgetRequest.id}
                                files={budgetRequest.files}
                            />
                        ) : (
                            <>
                                <Typography sx={{ fontWeight: 700 }}>
                                    {translate('budgetRequestForm.files.title')}
                                </Typography>

                                <Typography color="text.secondary">
                                    {translate(
                                        'budgetRequestForm.files.addAfterCreate'
                                    )}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Divider sx={{ mt: 3 }} />

                    <FormFooter
                        canDelete={!!onDelete}
                        deleteDialogText={translate(
                            'budgetRequestForm.deleteDialog.text'
                        )}
                        deleteDialogTitle={translate(
                            'budgetRequestForm.deleteDialog.title'
                        )}
                        submitButtonText={
                            // Editing uses the default 'save' label
                            isEdit
                                ? undefined
                                : translate('budgetRequestForm.submitButton')
                        }
                        onClose={onClose}
                        onDelete={onDelete}
                    />
                </form>
            </Box>
        </FormProvider>
    );
};
